class WiroModule {
  constructor(name, parentEl) {
    this.name = name || "wiroModule";
    this.parentEl = parentEl;
  }

  bindEvents() {}
  unbindEvents() {}
  render() {}

}

class WiroModuleHandler extends HTMLElement {
  /**
   * @param {WiroModule[]} modules
   */
  constructor(modules) {
    super();

    this.modules = {};
    for (const Module of modules) {
      const newModule = new Module(this);
      this.modules[newModule.name] = newModule;
    }
  }

  connectedCallback() {
    Object.values(this.modules).forEach((module) => module.bindEvents());
  }

  disconnectedCallback() {
    Object.values(this.modules).forEach((module) => module.unbindEvents());
  }
}

class WiroHTMLRenderer extends WiroModuleHandler {
  /**
   * @param {WiroModule[]} modules
   */
  constructor(modules = []) {
    super(modules);
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  prepareStringForRender(string) {
    if (!string) return;
    const domParser = new DOMParser();
    const dom = domParser.parseFromString(string, "text/html");
    return dom.body.firstChild;
  }

  render(dom) {
    if (!dom) return;
    if (typeof dom === "string") dom = this.prepareStringForRender(dom);
    this.innerHTML = "";
    this.appendChild(dom);
    Object.values(this.modules).forEach((module) => module.render());
  }
}

class RequestUtilityModule extends WiroModule {
  constructor(parentEl) {
    super("requestUtility", parentEl);
  }
  debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }
}

class SearchspringAPIModule extends WiroModule {
  constructor(parentEl) {
    super("searchspring", parentEl);
  }

  get userId() {
    let id = this.getCookie("ssUserId");
    if (!id) id = this.setCookie("ssUserId", this.generateUUIDv4());
    return id;
  }
  get cartProducts() {
    return this.getCookie("ssCartProducts") || "";
  }
  get viewedProducts() {
    return this.getCookie("ssViewedProducts") || "";
  }
  get sessionId() {
    let id = this.getCookie("ssSessionIdNamespace");
    if (!id) id = this.setCookie("ssSessionIdNamespace", this.generateUUIDv4());
    return id;
  }
  get pageLoadId() {
    if (!window.searchspringPageLoadId) window.searchspringPageLoadId = this.generateUUIDv4();
    return window.searchspringPageLoadId;
  }
  get recentlyViewedFromCookie() {
    const value = document.cookie.split("; ").find((row) => row.startsWith("ssViewedProducts="));
    if (!value) return [];
    try {
      const raw = decodeURIComponent(value.split("=")[1]);
      if (raw.startsWith("[")) {
        return JSON.parse(raw);
      }
      return raw.split(",");
    } catch (e) {
      return [];
    }
  }
  get segmentedTagParam() {
    const params = new URLSearchParams(window.location.search);
    const tag = params.get("tag");
    if (!tag || !tag.startsWith("merch.segment/")) return;
    return tag;
  }
  get siteId() {
    return "tr8yw9";
  }
  get cookieExpiryDays() {
    return 3;
  }

  getCookie(name) {
    if (!name) return;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  }

  setCookie(name, value) {
    if (!name || !value) return;
    const date = new Date();
    date.setTime(date.getTime() + this.cookieExpiryDays * 24 * 60 * 60 * 1000);
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  }

  decodeHtml(html) {
    if (!html) return;
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  generateUUIDv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  parseObjectIntoQueryParams(object) {
    let urlParams = "";
    Object.entries(object).forEach(([key, value], index) => {
      const prefix = index === 0 ? "?" : "&";
      urlParams = `${urlParams}${prefix}${key}=${value}`;
    });
    return urlParams;
  }

  toQueryParams(data, prefix = "") {
    const queryParams = [];
    for (const { key, value } of data) {
      const paramKey = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === "object") {
        queryParams.push(this.toQueryParams(value, paramKey));
      } else {
        queryParams.push(`${encodeURIComponent(paramKey)}=${encodeURIComponent(value)}`);
      }
    }
    return queryParams.join("&");
  }

  /**
   * Helper that sends API requests to Searchspring
   * https://docs.searchspring.com/reference/get-search
   *
   * @param additionalParams Key value store of additional parameters to add to the request
   *
   * @return The response data
   */
  async sendSearchRequest(additionalParams = {}) {
    const requiredParams = [
      { key: "siteId", value: this.siteId },
      { key: "resultsFormat", value: "json" },
      { key: "userId", value: this.userId },
      { key: "sessionId", value: this.sessionId },
      { key: "pageLoadId", value: this.pageLoadId },
      { key: "domain", value: window.location.href },
    ];
    let urlParams = "";
    // Object.entries({ ...requiredParams, ...additionalParams }).forEach(([key, value], index) => {
    //   const prefix = index === 0 ? "?" : "&"; // The first param needs ?, all following ones need &
    //   urlParams = `${urlParams}${prefix}${key}=${value}`; // Concat on this param
    // });
    urlParams = this.toQueryParams([...requiredParams, ...additionalParams]);
    const url = `https://${this.siteId}.a.searchspring.io/api/search/search.json?${urlParams}`;

    const payload = {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    };

    const response = await fetch(url, payload);
    let data = await response.json();
    if (typeof data === "string") data = JSON.parse(data);
    if (Array.isArray(data)) data = data[0];
    return data;
  }
  async sendRecommendRequest(profileTag, additionalParams = {}, limit = 10) {
    const requiredParams = {
      siteId: this.siteId,
      resultsFormat: "json",
      userId: this.userId,
      sessionId: this.sessionId,
      pageLoadId: this.pageLoadId,
      domain: window.location.href,
      profiles: [
        {
          tag: profileTag,
          limit,
        },
      ],
    };
    const url = `https://${this.siteId}.a.searchspring.io/boost/${this.siteId}/recommend`;

    const payload = {
      method: "POST",
      body: JSON.stringify({ ...requiredParams, ...additionalParams }),
      headers: { "Content-Type": "application/json" },
    };

    const response = await fetch(url, payload);
    let data = await response.json();
    if (typeof data === "string") data = JSON.parse(data);
    if (Array.isArray(data)) data = data[0];
    return data;
  }
  async sendAutocompleteRequest(searchTerm, additionalParams = {}, limit = 10) {
    const requiredParams = {
      siteId: this.siteId,
      resultsFormat: "json",
      userId: this.userId,
      sessionId: this.sessionId,
      pageLoadId: this.pageLoadId,
      domain: window.location.href,
      q: encodeURIComponent(searchTerm),
      suggestionCount: limit,
      language: "en",
    };
    const urlParams = this.parseObjectIntoQueryParams({ ...requiredParams, ...additionalParams });
    const url = `https://${this.siteId}.a.searchspring.io/api/suggest/query${urlParams}`;

    const payload = {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    };

    const response = await fetch(url, payload);
    let data = await response.json();
    if (typeof data === "string") data = JSON.parse(data);
    if (Array.isArray(data)) data = data[0];
    return data;
  }
}

class SearchspringCarouselHandler extends WiroHTMLRenderer {
  constructor(modules = [], profileTag = "home", productIds, productCardComponent) {
    super([SearchspringAPIModule, ...modules]);

    this.profileTag = this.dataset.profileTag || profileTag || "";
    this.productIds = productIds || this.dataset.productIds;
    this.productCardComponent = productCardComponent || this.dataset.productCardComponent || "wiro-product-card";
    this.carouselOptions = this.parseCarouselOptions();
    this.limit = this.dataset.limit || 20;
  }

  async connectedCallback() {
    const carouselData = {};
    if (this.productIds) carouselData.products = this.productIds;

    const additionalParams = {
      ...(this.productIds && { products: this.productIds.split(",") }),
    };

    const data = await this.modules.searchspring.sendRecommendRequest(this.profileTag, additionalParams, this.limit);

    const domString = this.getDomString(data.results);

    const dom = this.prepareStringForRender(domString);

    const productCards = this.getProductCards(data.results);

    const insertionPoint = dom.querySelector("[data-card-container]");

    productCards.forEach((card) => insertionPoint.appendChild(card));

    this.render(dom);

    this.carousel = new Splide(this.querySelector(".splide"), this.getCarouselOptions());
    if (window.splide && window.splide.Extensions && window.splide.Extensions.Grid) {
      this.carousel.mount({ Grid: window.splide.Extensions.Grid });
    } else {
      this.carousel.mount();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.carousel.destroy(true);
  }

  parseCarouselOptions() {
    const defaultOptions = {
      type: "loop",
      perPage: 8,
      arrows: false,
      pagination: false,
      breakpoints: {
        640: { perPage: 2 },
      },
    };
    if (!this.dataset.carouselOptions) return defaultOptions;

    try {
      const customOptions = JSON.parse(this.dataset.carouselOptions);
      return { ...defaultOptions, ...customOptions };
    } catch (error) {
      console.warn("Invalid JSON in data-carousel-options, using defaults:", error);
      return defaultOptions;
    }
  }

  getCarouselOptions() {
    return this.carouselOptions;
  }

  getProductCards(results) {
    return results.map((product) => {
      const card = document.createElement(this.productCardComponent);
      card.setProduct(product);
      const cardSplideWrapper = this.prepareStringForRender(
        `<li class="splide__slide tw-w-[172px] max-sm:!tw-w-[46%]"></li>`,
      );
      cardSplideWrapper.appendChild(card);
      return cardSplideWrapper;
    });
  }

  getDomString(results) {
    if (!results.length) return `<p>No results found</p>`;

    return `
      <section class="splide gradient-edges" aria-labelledby="carousel-heading">
        <div class="splide__track">
          <ul class="splide__list" data-card-container>
          </ul>
        </div>
      </section>
    `;
  }
}
if (!customElements.get("searchspring-carousel-handler"))
  customElements.define("searchspring-carousel-handler", SearchspringCarouselHandler);

class WiroProductCard extends WiroHTMLRenderer {
  constructor() {
    super([]);
  }
  setProduct(product) {
    this.product = product;
  }
  parseBoostAPIProductData() {
    const p = this.product || {};
    if (p.mappings && p.mappings.core && p.attributes) {
      const core = p.mappings.core || {};
      const attributes = p.attributes || {};
      if (!this.product.id && (core.uid || core.id)) this.product.id = core.uid || core.id;
      return {
        name: core.name || "",
        url: core.url || "#",
        imageUrl: core.imageUrl || core.thumbnailImageUrl || "",
        price: Number(core.price) || 0,
        msrp: Number(core.msrp) || 0,
        brand: core.brand || "",
        okendo_summarydata: attributes.mfield_okendo_summarydata || null,
        size_available: attributes.ss_size_in_stock || null,
        brand_collab: attributes.mfield_custom_brand_collaboration || null,
        images: attributes.images || [],
      };
    } else {
      const uid = p.uid;
      const url = p.url || (p.handle ? `/products/${p.handle}` : "#");
      const okendo_summarydata = p ? p.mfield_okendo_summarydata : null;
      const size_available = p ? p.ss_size_in_stock : null;
      const imageUrl = p.thumbnailImageUrl;
      const images = p.images || [];
      let price = 0;
      if (p.price) price = Number(p.price);

      let msrp = 0;
      if (p.msrp) msrp = Number(p.msrp);
      this.product.id = uid;

      const brand_collab = p ? p.mfield_custom_brand_collaboration : null;
      return {
        name: p.name || p.title || "",
        url,
        imageUrl,
        price: Number(price) || 0,
        msrp: Number(msrp) || 0,
        brand: p.vendor || p.brand || "",
        okendo_summarydata,
        size_available,
        brand_collab,
        images,
      };
    }
  }
  connectedCallback() {
    if (!this.product) return;
    const { name, url, imageUrl, price } = this.parseBoostAPIProductData();

    const card = this.prepareStringForRender(`
      <a href="${url}">
        <div class="card tw-flex tw-items-center tw-text-center">
          <img class="tw-size-[130px] sm:tw-size-30" src="${imageUrl}" alt="${name}">
          <p class="tw-text-center tw-text-small tw-mt-2 tw-mb-1 sm:tw-line-clamp-2">${name}</p>
          <p class="tw-font-semibold tw-mt-auto tw-text-small">${this.formatPrice(price)}</p>
        </div>
      </a>
    `);

    this.render(card);
  }

  formatPrice(price) {
    return `£${price}`;
  }
}
if (!customElements.get("wiro-product-card")) customElements.define("wiro-product-card", WiroProductCard);
